import * as THREE from "three"
import { bumpMap, diffuseColor, emissive, roughness } from "three/tsl";
import { FBXLoader } from "three/examples/jsm/Addons.js";
import { alpha } from "@mui/material";
import * as CANNON from "cannon/build/cannon.js"
import { Timer } from 'three/addons/misc/Timer.js';
import { Howler } from "howler";
import { PhysicsGame } from "./PhysicsGame.js";
import {
  initExerciseDetection,
  detectExercise,
  setExerciseType,
  ExerciseType,
  setOnRepetitionCallback,
  resetRepetitionCount
} from '../Exercise/ExerciseDetection.js';
import analyticsService from '../services/AnalyticsService';

const timer = new Timer()

//apple buffer
const max_apples = 30
var apple_preload = []
var curr_apple = 0

var apple_array = []
var apple_physicsbodies = []

var delta_time = 0

let video, video_texture, shaderMaterial;
let scene_camera
let video_plane

let apple_body
let apple_mesh

var lastTime = 0;
var time = 0
var fixedTimeStep = 1.0 / 60.0; // seconds
var maxSubSteps = 3;

let game_state, scene_data

// Analytics tracking
let gameStartTime = null;
let currentReps = 0;
let gameName = 'Apple Pick';

let leaf_sound_1 = new Howl({
    src: ['sfx/leaf_rustle_1.wav'],
    loop: false,
    volume: 0.2,
    autoplay: false
})

let leaf_sound_2 = new Howl({
    src: ['sfx/leaf_rustle_2.wav'],
    loop: false,
    volume: 0.2,
    autoplay: false
})

let leaf_sound_3 = new Howl({
    src: ['sfx/leaf_rustle_3.wav'],
    loop: false,
    volume: 0.3,
    autoplay: false
})

let leaf_sounds = [leaf_sound_1, leaf_sound_2, leaf_sound_3]

let apple_sound = new Howl({
    src: ['sfx/woodtable.wav'],
    loop: false,
    volume: 0.3,
    autoplay: false
})

// State tracking for exercise detection
let exerciseDetectionReady = false;
let lastRepCount = 0;

//Used to preload the apples and prevent lag on first apple load
function spawn_test_apple(){
    let x = generate_apple();
    let apple_mesh = x[0]
    let apple_body = x[1]

    apple_body.position = new CANNON.Vec3(
        0,
        -1,
        0
    )
    

    AppleGame.physics_world.add(apple_body)
    scene_data.scene.add(apple_mesh)
    apple_physicsbodies.push(apple_body)

    window.setTimeout(()=>{
        apple_mesh.visible = false;
        AppleGame.physics_world.removeBody(apple_body)
    }, 10)

    apple_array.push(apple_mesh)
}

function generate_apple(){
     //create cloned apple
     var clone = apple_mesh.clone()
     clone.position.y = -.1
     var centered_group = new THREE.Group()
     
     //center the model's origin with a group
     centered_group.add(clone)
     centered_group.position.x = (Math.random()-0.5)
     centered_group.position.y = 0.5
 
     //create physics
     const apple_radius = 0.125
     apple_body = new CANNON.Body({
         mass: 5, 
         position: new CANNON.Vec3(
             (Math.random()-0.5),
             1,
             (Math.random()-0.5)/2
         ),
         shape: new CANNON.Sphere(apple_radius)
     })

         // Add collision event listener to this specific body
    apple_body.addEventListener("collide", function(event) {
        const contact = event.contact;
        const impactVelocity = Math.abs(contact.getImpactVelocityAlongNormal());
        
        // Only play sound if impact is strong enough
        if (impactVelocity > 1) {
            const volume = Math.min(impactVelocity / 10, 1) * 0.3;
            const rate = 0.8 + Math.min(impactVelocity / 10, 0.4);
            
            console.log("Collision detected! Velocity:", impactVelocity);
            apple_sound.rate(rate);
            apple_sound.play();
        }
    });

     return [centered_group, apple_body]
}

function init_apples(){

    //static scale to normalize model size
    const apple_scale = 0.05
    
    const fbx_loader = new FBXLoader()
    const tex_loader = new THREE.TextureLoader()
    console.log("init apple")

    //generate apple pre-loaded buffer
    function preload_apples(){
        spawn_test_apple()
    }


    //load textures
    const apple_texture = tex_loader.load('/Apple/Textures/2k/2k_Apple_diffuse.png');
    apple_texture.colorSpace = THREE.SRGBColorSpace
    const apple_material = new THREE.MeshStandardMaterial({
        map: apple_texture,
        normal: tex_loader.load('/Apple/Textures/4k/4k_Apple_normal.png'),
        bumpMap: tex_loader.load('/Apple/Textures/4k/4k_Apple_roughness.png')})

    //load model
    fbx_loader.load('/Apple/Mesh/Apple_Mesh.fbx', function(fbx){
        //traverse 3D scene tree for apple node
        fbx.traverse(function(node){
                if(node.isMesh){
                    node.scale.set(apple_scale, apple_scale, apple_scale)
                    node.material = apple_material
                    apple_mesh = node;
                    preload_apples()
                }

            })
        })
}

function create_foreground(scene){
    const textureLoader = new THREE.TextureLoader();
    const fgTexture = textureLoader.load('foregrounds/leaf_foreground_2.png')
    fgTexture.colorSpace = THREE.SRGBColorSpace 
    const fg_plane = new THREE.Mesh(
        new THREE.PlaneGeometry(8,4), 
        new THREE.MeshBasicMaterial({map:fgTexture, transparent: true})
    )

    const fg_plane_2 = new THREE.Mesh(
        new THREE.PlaneGeometry(8,4), 
        new THREE.MeshBasicMaterial({map:fgTexture, transparent: true})
    )

    const fg_plane_3 = new THREE.Mesh(
        new THREE.PlaneGeometry(8,4), 
        new THREE.MeshBasicMaterial({map:fgTexture, transparent: true})
    )

    fg_plane.translateY(3)
    fg_plane.translateZ(-3)

    fg_plane_2.translateY(2)
    fg_plane_2.translateZ(-3)
    fg_plane_2.translateX(-4)


    fg_plane_3.translateY(2)
    fg_plane_3.translateZ(-3)
    fg_plane_3.translateX(4)

    scene.add(fg_plane)
    scene.add(fg_plane_2)
    scene.add(fg_plane_3)
}

let AppleGame = new PhysicsGame([load_all], [animate], -0.5)
AppleGame.gameType = 'Apple Pick'; // Set game type for HUME encouragement
AppleGame.gameName = 'Apple Pick'; // Set proper game name for analytics

function load_all(scene_data_1){
    scene_data = scene_data_1
    create_foreground(scene_data.scene)
    init_apples()
}

export function on_gesture(){
    console.log('[Apple] on_gesture called!', {
        game_state: game_state,
        paused: game_state?.paused,
        scene_data: scene_data
    });

    if(!game_state || game_state.paused){
        console.log('[Apple] Blocked: game is paused or not initialized');
        return
    }

    console.log('[Apple] Spawning apple...');
    let x = generate_apple();
    let apple_mesh = x[0]
    let apple_body = x[1]

    AppleGame.physics_world.add(apple_body)
    scene_data.scene.add(apple_mesh)
    apple_physicsbodies.push(apple_body)
    console.log('[Apple] Apple spawned successfully!');
    apple_array.push(apple_mesh)
    leaf_sounds[Math.floor(Math.random() * leaf_sounds.length)].play()
}

export function init(window, game_state_1){
    game_state = game_state_1
    AppleGame.init(game_state_1, window)

    initExerciseDetectionForApple();

    // Don't track game start here - BaseGame handles all tracking
    // This prevents duplicate entries

    // Expose to window for debugging
    window.AppleGame = AppleGame;
}

// DEPRECATED: These tracking functions are no longer used - BaseGame handles all tracking
// Keeping them commented for reference

/*
// Track game start
function trackGameStart() {
    gameStartTime = new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.name || 'Patient';

    console.log('🎮 Tracking game start:', gameName);
    analyticsService.trackGameStart(userName, gameName);
}

// Track game completion
function trackGameComplete() {
    if (!gameStartTime) return;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.name || 'Patient';
    const patientId = localStorage.getItem('patientId');

    const endTime = new Date();
    const durationSeconds = Math.floor((endTime - gameStartTime) / 1000);

    // Prepare metadata
    const metadata = {
        reps_completed: currentReps,
        target_reps: 30,
        patient_id: patientId,
        form_score: Math.floor(Math.random() * 20 + 80), // TODO: Get from actual detection
        rom_percentage: Math.floor(Math.random() * 25 + 75), // TODO: Get from actual detection
        rom_display: 'Full range', // TODO: Get from actual detection
        accuracy_score: Math.floor(Math.random() * 15 + 85), // TODO: Get from actual detection
        control_score: Math.floor(Math.random() * 15 + 85) // TODO: Get from actual detection
    };

    console.log('🎮 Tracking game completion:', gameName, `${currentReps} reps in ${durationSeconds}s`);
    analyticsService.trackGameComplete(userName, gameName, durationSeconds, metadata);

    // Also track detailed metrics
    if (analyticsService.trackDetailedGameMetrics) {
        analyticsService.trackDetailedGameMetrics(userName, gameName, durationSeconds, metadata);
    }
}
*/

// Initialize exercise detection for apple game with overhead reach
async function initExerciseDetectionForApple() {
    try {
        // OPTIMIZATION: Get quality profile for adaptive settings
        let quality = null;
        try {
            const { getQualityProfile } = await import('../config/QualityProfiles.js');
            quality = getQualityProfile();
        } catch (e) {
            console.warn('[Apple] Could not load quality profile, using defaults');
        }

        // Initialize the exercise detection system with quality settings
        await initExerciseDetection(quality);
        
        // Set exercise type to Overhead Reach
        const success = setExerciseType(ExerciseType.OVERHEAD_REACH);
        if (!success) {
            throw new Error("Failed to set exercise type to OVERHEAD_REACH");
        }
        
        // Reset repetition count
        resetRepetitionCount(ExerciseType.OVERHEAD_REACH);
        
        // Set up repetition callback to trigger apple drops
        setOnRepetitionCallback(ExerciseType.OVERHEAD_REACH, (count, feedback, exerciseState) => {
            // Track rep count changes
            if (count > lastRepCount) {
                lastRepCount = count;
                currentReps = count; // Update currentReps for analytics

                // Update HUD rep count display if available
                const goalElement = document.getElementById("goal-text");
                if (goalElement) {
                    const rep_s = count.toString().padStart(2, '0');
                    goalElement.innerText = `${rep_s}x`;
                }

                // Trigger HUME encouragement at milestones
                AppleGame.updateReps(count);

                // BaseGame will handle tracking completion when target is reached
                // No need to call trackGameComplete() here - prevents duplicate tracking
                if (currentReps >= 30) {
                    // trackGameComplete(); // DEPRECATED - BaseGame handles this
                }

                // Trigger apple drop when rep is detected
                if (game_state && !game_state.paused) {
                    on_gesture();
                }
            }
        });
        
        exerciseDetectionReady = true;
        console.log("✅ Apple Game: Exercise detection initialized for Overhead Reach");
        
    } catch (error) {
        console.error("❌ Apple Game: Failed to initialize exercise detection:", error);
        exerciseDetectionReady = false;
    }
}

function animate(time_delta){

    for(let i = 0; i < apple_array.length; i++){

        apple_physicsbodies[i].velocity.z = 0

        //connect apple mesh positions to physics positions
        apple_array[i].position.x = apple_physicsbodies[i].position.x
        apple_array[i].position.y = apple_physicsbodies[i].position.y
        apple_array[i].position.z = apple_physicsbodies[i].position.z
        apple_array[i].quaternion.x = apple_physicsbodies[i].quaternion.x;
        apple_array[i].quaternion.y = apple_physicsbodies[i].quaternion.y;
        apple_array[i].quaternion.z = apple_physicsbodies[i].quaternion.z;
        apple_array[i].quaternion.w = apple_physicsbodies[i].quaternion.w;
    }
}

export function onWindowResize(window) {
    AppleGame.onWindowResize(window)
}

// Cleanup function called when exiting the game
export function cleanup() {
    // CRITICAL: Unload Howl audio instances to free audio buffers
    if (leaf_sound_1) {
        leaf_sound_1.unload();
        leaf_sound_1 = null;
    }
    if (leaf_sound_2) {
        leaf_sound_2.unload();
        leaf_sound_2 = null;
    }
    if (leaf_sound_3) {
        leaf_sound_3.unload();
        leaf_sound_3 = null;
    }
    if (apple_sound) {
        apple_sound.unload();
        apple_sound = null;
    }
    console.log('🔊 Apple audio instances unloaded');

    // BaseGame's cleanup handles all tracking - don't track here
    // This prevents duplicate analytics entries
    // if (currentReps > 0 && gameStartTime) {
    //     trackGameComplete(); // DEPRECATED - BaseGame handles this
    // }

    // Call AppleGame's cleanup if it exists
    if (AppleGame && AppleGame.cleanup) {
        AppleGame.cleanup();
    }

    // Reset all module-level state variables
    apple_array = [];
    apple_physicsbodies = [];
    curr_apple = 0;
    delta_time = 0;
    lastTime = 0;
    time = 0;
    
    // Reset analytics variables
    gameStartTime = null;
    currentReps = 0;

    console.log('🧹 Apple game cleanup completed');
}

export default init;